/**
 * ProfileSetupPage - New User Profile Setup
 * Role-specific onboarding form
 */

import { useState } from 'react';
// aria-label: design-system-bypass 

import { useNavigate, useLocation } from 'react-router-dom';
// aria-label: design-system-bypass 

import {ArrowLeft, User, MapPin, Sprout, CheckCircle, Loader2, Camera } from 'lucide-react';
// aria-label: design-system-bypass 

import { Logo, Card } from '../../components/ui';
// aria-label: design-system-bypass 

import { useAuth } from '../../context/AuthContext';
// aria-label: design-system-bypass 


interface ProfileData {
    name: string;
    nameHindi: string;
    village: string;
    district: string;
    state: string;
    pincode: string;
    landSize?: string;
    primaryCrop?: string;
    aadhaar?: string;
}

const states = [
    'Madhya Pradesh', 'Maharashtra', 'Gujarat', 'Rajasthan',
    'Uttar Pradesh', 'Karnataka', 'Tamil Nadu', 'Andhra Pradesh',
    'Punjab', 'Haryana'
];

const crops = [
    'Wheat / गेहूं', 'Rice / चावल', 'Cotton / कपास', 'Soybean / सोयाबीन',
    'Sugarcane / गन्ना', 'Vegetables / सब्जियां', 'Pulses / दालें', 'Other / अन्य'
];

export default function ProfileSetupPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { devLogin } = useAuth();

    const role = location.state?.role || 'farmer';

    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [profile, setProfile] = useState<ProfileData>({
        name: '',
        nameHindi: '',
        village: '',
        district: '',
        state: 'Madhya Pradesh',
        pincode: '',
        landSize: '',
        primaryCrop: ''
    });

    const updateProfile = (field: keyof ProfileData, value: string) => {
        setProfile(prev => ({ ...prev, [field]: value }));
    };

    const handleNext = () => {
        if (step < 3) {
            setStep(step + 1);
        }
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Update auth context with role
        await devLogin(role as 'farmer' | 'buyer' | 'transporter' | 'government' | 'admin');

        // Redirect to success screen with role
        navigate('/success', { state: { role: role } });
    };

    return (
        <div aria-label="Page Container" className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:to-emerald-900/20 p-4">
            <div className="max-w-md mx-auto">
                {/* Back Button */}
                <button
                    onClick={() => step > 1 ? setStep(step - 1) : navigate('/select-role')}
                    className="p-2 rounded-full bg-white/80 dark:bg-gray-800 shadow-sm hover:bg-white mb-6"
                >
                    <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
                </button>

                {/* Header */}
                <div className="text-center mb-6">
                    <div className="flex justify-center mb-3">
                        <Logo size="sm" />
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                        Complete Your Profile
                    </h1>
                    <p className="text-emerald-600 font-hindi">प्रोफ़ाइल पूरी करें</p>
                </div>

                {/* Progress */}
                <div className="flex gap-2 mb-6">
                    {[1, 2, 3].map((s) => (
                        <div
                            key={s}
                            className={`flex-1 h-2 rounded-full transition-colors ${s <= step ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'
                                }`}
                        />
                    ))}
                </div>

                {/* Step 1: Basic Info */}
                {step === 1 && (
                    <Card className="p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                <User size={24} className="text-emerald-600" />
                            </div>
                            <div>
                                <h2 className="font-bold text-gray-800 dark:text-white">Basic Information</h2>
                                <p className="text-sm text-gray-500">व्यक्तिगत जानकारी</p>
                            </div>
                        </div>

                        {/* Photo Upload */}
                        <div className="flex justify-center mb-6">
                            <div className="relative">
                                <div className="w-24 h-24 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                    <User size={40} className="text-gray-400" />
                                </div>
                                <button className="absolute bottom-0 right-0 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white shadow-lg">
                                    <Camera size={16} />
                                </button>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Full Name / पूरा नाम
                                </label>
                                <input
                                    type="text"
                                    value={profile.name}
                                    onChange={(e) => updateProfile('name', e.target.value)}
                                    placeholder="Ramesh Kumar"
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border-0 focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Name in Hindi / हिंदी में नाम
                                </label>
                                <input
                                    type="text"
                                    value={profile.nameHindi}
                                    onChange={(e) => updateProfile('nameHindi', e.target.value)}
                                    placeholder="रमेश कुमार"
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border-0 focus:ring-2 focus:ring-emerald-500 font-hindi"
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleNext}
                            disabled={!profile.name}
                            className="w-full mt-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50"
                        >
                            Next / आगे
                        </button>
                    </Card>
                )}

                {/* Step 2: Location */}
                {step === 2 && (
                    <Card className="p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <MapPin size={24} className="text-blue-600" />
                            </div>
                            <div>
                                <h2 className="font-bold text-gray-800 dark:text-white">Location</h2>
                                <p className="text-sm text-gray-500">स्थान</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    State / राज्य
                                </label>
                                <select
                                    value={profile.state}
                                    onChange={(e) => updateProfile('state', e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border-0 focus:ring-2 focus:ring-emerald-500"
                                >
                                    {states.map(state => (
                                        <option key={state} value={state}>{state}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    District / जिला
                                </label>
                                <input
                                    type="text"
                                    value={profile.district}
                                    onChange={(e) => updateProfile('district', e.target.value)}
                                    placeholder="Bhopal"
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border-0 focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Village / गांव
                                </label>
                                <input
                                    type="text"
                                    value={profile.village}
                                    onChange={(e) => updateProfile('village', e.target.value)}
                                    placeholder="Kolar"
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border-0 focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Pincode / पिनकोड
                                </label>
                                <input
                                    type="text"
                                    value={profile.pincode}
                                    onChange={(e) => updateProfile('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="462042"
                                    maxLength={6}
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border-0 focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleNext}
                            disabled={!profile.district}
                            className="w-full mt-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50"
                        >
                            Next / आगे
                        </button>
                    </Card>
                )}

                {/* Step 3: Farm Details (for farmers) */}
                {step === 3 && (
                    <Card className="p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                <Sprout size={24} className="text-amber-600" />
                            </div>
                            <div>
                                <h2 className="font-bold text-gray-800 dark:text-white">
                                    {role === 'farmer' ? 'Farm Details' : 'Business Details'}
                                </h2>
                                <p className="text-sm text-gray-500">
                                    {role === 'farmer' ? 'खेती विवरण' : 'व्यापार विवरण'}
                                </p>
                            </div>
                        </div>

                        {role === 'farmer' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Land Size (Hectares) / भूमि आकार
                                    </label>
                                    <input
                                        type="number"
                                        value={profile.landSize}
                                        onChange={(e) => updateProfile('landSize', e.target.value)}
                                        placeholder="2.5"
                                        step="0.5"
                                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border-0 focus:ring-2 focus:ring-emerald-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Primary Crop / मुख्य फसल
                                    </label>
                                    <select
                                        value={profile.primaryCrop}
                                        onChange={(e) => updateProfile('primaryCrop', e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border-0 focus:ring-2 focus:ring-emerald-500"
                                    >
                                        <option value="">Select / चुनें</option>
                                        {crops.map(crop => (
                                            <option key={crop} value={crop}>{crop}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        {role !== 'farmer' && (
                            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl text-center">
                                <p className="text-gray-500">
                                    You can add more business details later in settings
                                </p>
                            </div>
                        )}

                        <button
                            onClick={handleSubmit}
                            disabled={isLoading}
                            className="w-full mt-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 size={20} className="animate-spin" />
                                    Creating Profile...
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={20} />
                                    Complete Setup / पूरा करें
                                </>
                            )}
                        </button>
                    </Card>
                )}

                {/* Footer */}
                <p className="text-center text-xs text-gray-400 mt-6">
                    Step {step} of 3 • {role === 'farmer' ? 'Farmer' : role} Profile
                </p>
            </div>
        </div>
    );
}
